import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export type FormLayoutAsideWidth = 'default' | 'wide';

export interface FormLayoutProps {
  /** Main column: the big sections. */
  children: ReactNode;
  /** Optional right column: status, organisation, metadata. */
  aside?: ReactNode;
  /**
   * `default` is a 320px rail from `lg` up. `wide` fits a preview canvas: `minmax(360px, 420px)`
   * from `xl` up and stacked under the main column below that.
   */
  asideWidth?: FormLayoutAsideWidth;
  className?: string;
}

const ASIDE_COLUMNS: Record<FormLayoutAsideWidth, string> = {
  default: 'lg:grid-cols-[minmax(0,1fr)_320px]',
  wide: 'xl:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]',
};

/** Two-column form page (main + aside) that stacks on small screens. */
export function FormLayout({
  children,
  aside,
  asideWidth = 'default',
  className,
}: FormLayoutProps) {
  return (
    <div className={cn('grid gap-6', aside ? ASIDE_COLUMNS[asideWidth] : '', className)}>
      <div className="flex min-w-0 flex-col gap-6">{children}</div>
      {aside ? <div className="flex min-w-0 flex-col gap-6">{aside}</div> : null}
    </div>
  );
}

export interface FormSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  /** `data-tour` value when a guided tour points at this section. */
  tour?: string;
  /** Element id, so a link can open the page at this section. */
  id?: string;
  /**
   * The title row toggles the body, for long optional sections (FAQ, metadata) the user fills
   * in later. Closed by default; `open`/`onOpenChange` control it, `defaultOpen` seeds it.
   */
  collapsible?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FormSection({
  title,
  description,
  children,
  className,
  tour,
  id,
  collapsible = false,
  defaultOpen,
  open,
  onOpenChange,
}: FormSectionProps) {
  if (!collapsible) {
    return (
      <Card id={id} className={className} data-tour={tour}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">{children}</CardContent>
      </Card>
    );
  }
  return (
    <Collapsible asChild open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <Card id={id} className={className} data-tour={tour}>
        <CardHeader>
          <CollapsibleTrigger className="group/section -m-1 flex w-full items-center justify-between gap-3 rounded-md p-1 text-left leading-none font-semibold">
            {title}
            <ChevronDown
              aria-hidden
              className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[state=open]/section:rotate-180"
            />
          </CollapsibleTrigger>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CollapsibleContent asChild>
          <CardContent className="flex flex-col gap-4">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export interface FormActionsProps {
  isSubmitting: boolean;
  isDirty?: boolean;
  onCancel?: (() => void) | undefined;
  submitLabel?: string;
  /** Extra buttons on the left, e.g. delete. */
  secondary?: ReactNode;
  /** Sticks to the bottom of the viewport on long forms (default true). */
  sticky?: boolean;
  /** `data-tour` value for the bar when a guided tour points at saving. */
  tour?: string;
}

/** Save/cancel bar. Submit is disabled until the form is dirty so accidental saves are impossible. */
export function FormActions({
  isSubmitting,
  isDirty = true,
  onCancel,
  submitLabel,
  secondary,
  sticky = true,
  tour,
}: FormActionsProps) {
  const { t } = useTranslation('common');
  return (
    <div
      className={cn(
        'bg-background/95 supports-[backdrop-filter]:bg-background/70 flex items-center justify-between gap-2 border-t py-3 backdrop-blur',
        sticky && 'sticky bottom-0 z-10',
      )}
      data-tour={tour}
    >
      <div>{secondary}</div>
      <div className="flex items-center gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            {t('actions.cancel')}
          </Button>
        ) : null}
        <Button type="submit" disabled={isSubmitting || !isDirty}>
          {isSubmitting ? t('actions.saving') : (submitLabel ?? t('actions.save'))}
        </Button>
      </div>
    </div>
  );
}
